import { Component, ErrorHandler, Inject, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { LadataanService } from '../_jaettu-angular/service/ladataan.service'

import { MatSnackBar } from '@angular/material/snack-bar'
import { KirjanpidonPeruste } from '@lemon/core-firebase/model/kayttaja'
import { FirestoreTosite, PaymentStatus } from '@lemon/core-firebase/model/tosite'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { DateService } from '@lemon/core/service/date.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { TositeService } from 'app/_angular/service/tosite/tosite.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Subject, takeUntil } from 'rxjs'

interface MaksettuMuuallaLomake {
  maksutapa: number | null
  tositteenPvm: Date | null
}

export interface MaksutMaksutapaDialogData {
  kuitti: FirestoreTosite
  milla: 'omalla' | 'yrityksen-tililta'
  kirjanpidonPeruste: KirjanpidonPeruste
}

@Component({
  templateUrl: './maksut-maksutapa.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutMaksutapaDialog implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  voidaanMitatoida = false

  lomake = form(signal<MaksettuMuuallaLomake>({ maksutapa: null, tositteenPvm: null }), polku => {
    required(polku.maksutapa)
    required(polku.tositteenPvm)
  })

  maxDate: Date = new Date(2099, 11, 31)
  minDate: Date = new Date(2010, 0, 1)

  private _kuitti: FirestoreTosite = null

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: MaksutMaksutapaDialogData,
    private _dialogRef: MatDialogRef<MaksutMaksutapaDialog>,
    private _errorHandler: ErrorHandler,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _tositeKopioija: TositeKopioija,
    private _dateService: DateService,
    private _tositeService: TositeService,
    private _formValidationService: FormValidationService,
    private _kayttajaService: KayttajaService,
    private _timestampService: TimestampService,
  ) {
    this._kuitti = this._tositeKopioija.kopioiTosite(data.kuitti)
    this.maxDate = this._dateService.lisaaKuukausia(new Date(), 12)
    this.minDate = this._dateService.lisaaKuukausia(new Date(), -24)

    // combineLatest([this._tositeService.maksutavatObservable, this._kayttajaService.nykyinenAsiakasObservable]).pipe(
    //   filter(([kayttajanMaksutavat, asiakas]) => !!kayttajanMaksutavat && !!asiakas),
    //   take(1)
    // ).subscribe(([kayttajanMaksutavat, asiakas]) => {

    //   if (data.milla === 'omalla') {

    //     const generoitavia = asiakas.maksutavat.filter(m => m.)

    //     for () {

    //     }
    //   }

    //   if (maksutavat.filter(maksutapa => maksutapa.)) {

    //   }
    // })
  }

  ngOnInit() {
    arvonMuutokset(this.lomake.maksutapa, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this._kuitti.paymentMaksutapa = value
      })

    arvonMuutokset(this.lomake.tositteenPvm, { injector: this._injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        this._kuitti.paymentDate = this._dateService.dateToNumber(value)
      })

    asetaArvo(this.lomake.tositteenPvm, new Date())
  }

  private _saveInFlight = false
  async tallenna() {
    if (await this._kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._saveInFlight) {
      return
    }
    this._saveInFlight = true

    this._ladataanService.aloitaLataaminen()
    try {
      this._kuitti.paymentStatus = PaymentStatus.MAKSETTU_TOISAALLA
      if (this.data.kirjanpidonPeruste === KirjanpidonPeruste.MAKSU) {
        const paymentDateLocal = this._dateService.numberToLocalDate(this._kuitti.paymentDate)
        const paymentDateTs = this._timestampService.localDateToTimestamp(paymentDateLocal)

        this._kuitti.p = this._kuitti.paymentDate
        this._kuitti.pvm = paymentDateTs
        this._kuitti.localPvm = paymentDateLocal
      }
      await this._tositeService.tallennaKuitti(this._kuitti)
      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open(this._translationService.lokalisoi('yleiset.tuntematon-virhe'), 'OK', { duration: 10000 })
    } finally {
      this._saveInFlight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }
}
